/**
 * AIAdvisorContext — makes the ONE global AI advisor (the `AIAssistantWidget`
 * floating "Ask AI" panel) *grounded*.
 *
 * Before this, the widget was a generic chatbot: it sent only free text, so
 * the backend had to re-derive what you were looking at by keyword-matching
 * your message against the graph. Meanwhile the genuinely grounded AI calls
 * (`/ai/explain-issue`, `/ai/explain-path`) lived as one-off buttons that the
 * chat never saw. This context closes that gap: any surface can hand the
 * advisor the entity the user is currently looking at (a finding or a
 * resource) plus a snapshot of fields it already loaded, and the widget
 * attaches that context to every request so answers are grounded in the
 * actual thing on screen.
 *
 * Two ways to use it:
 *   - `askAbout(entity)` — set the focus AND pop the widget open (for an
 *     explicit "Ask AI about this" button in a drawer/detail page).
 *   - `setEntity(entity)` / `clearEntity()` — set/clear the focus silently
 *     (e.g. a detail page registering context on mount so that if the user
 *     opens the widget themselves it's already grounded).
 *
 * The `snapshot` is REAL platform data the caller already fetched — no
 * fabrication. The backend also best-effort re-fetches the live record by id
 * for freshness, but the snapshot guarantees grounding even if that fetch
 * 404s (e.g. pillar-local finding ids). See [[ui-universal-finding-drawer-2026-07-02]].
 */
import React, { createContext, useContext, useCallback, useState } from 'react';

export interface AdvisorEntity {
  /** What kind of thing the advisor should ground on. */
  kind: 'finding' | 'resource';
  /** Canonical id (csp_core.findings id / resource_id) when available. */
  id: string;
  /** Human label shown in the "grounded on …" chip. */
  label?: string;
  /**
   * A snapshot of already-loaded fields (severity, type, resource, etc.).
   * Sent verbatim to the backend as guaranteed grounding — real data the
   * caller already has, so it works even when a live id-fetch would 404.
   */
  snapshot?: Record<string, unknown>;
}

interface AIAdvisorContextValue {
  /** The entity the advisor is currently grounded on, or null. */
  entity: AdvisorEntity | null;
  /** Set the grounding focus without opening the widget. */
  setEntity: (e: AdvisorEntity | null) => void;
  /** Set the grounding focus AND open the advisor widget. */
  askAbout: (e: AdvisorEntity) => void;
  /** Clear the grounding focus (advisor falls back to page-level context). */
  clearEntity: () => void;
}

const AIAdvisorContext = createContext<AIAdvisorContextValue | null>(null);

/**
 * Consume the advisor context. Returns a no-op-safe fallback when used
 * outside the provider so a stray caller never crashes the tree.
 */
export const useAIAdvisor = (): AIAdvisorContextValue => {
  const ctx = useContext(AIAdvisorContext);
  if (!ctx) {
    return {
      entity: null,
      setEntity: () => {},
      askAbout: () => {},
      clearEntity: () => {},
    };
  }
  return ctx;
};

export const AIAdvisorProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [entity, setEntityState] = useState<AdvisorEntity | null>(null);

  const setEntity = useCallback((e: AdvisorEntity | null) => setEntityState(e), []);
  const clearEntity = useCallback(() => setEntityState(null), []);

  const askAbout = useCallback((e: AdvisorEntity) => {
    setEntityState(e);
    // The widget listens for this event (also fired by the top-nav button).
    window.dispatchEvent(new CustomEvent('open-ai-assistant'));
  }, []);

  return (
    <AIAdvisorContext.Provider value={{ entity, setEntity, askAbout, clearEntity }}>
      {children}
    </AIAdvisorContext.Provider>
  );
};

export default AIAdvisorProvider;
